// external modules
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import store from "store";

// internal modules
import { checkEmail } from "api/@auth/api_auth";
import { useInput } from "hooks/@common/useInput";
import { useAppDispatch } from "store/redux/modules";
import { login } from "store/redux/modules/account";
import { validateEmail } from "utils/@validate/utils_validate";
import * as nativeApi from "utils/nativeApi";

// components
import BtnV2 from "components/@common/BtnV2/BtnV2";
import ErrorMsgBox from "components/@common/ErrorMsgBox/ErrorMsgBox";
import PageTitle from "components/@common/PageTitle/PageTitle";
import { TextInput } from "components/@common/TextInput/TextInput";

// styles & types & assets
import type { Nullable } from "@types";
import { SvgKakao, SvgMail } from "assets/svg/Icon";
import { localStorageKeyLastSignInEmail } from "utils/(localStorage)";
import styles from "./Login.module.scss";
import { toaster } from "@becon-team/becon-fe-lib/my-toast";

export default function Login({
  onCloseLoginBottomSheet,
  email,
}: {
  onCloseLoginBottomSheet: () => void;
  email: Nullable<string>;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isEmailLogin, setIsEmailLogin] = useState(false);

  const lastSignInEmail = store.get(localStorageKeyLastSignInEmail);

  const [isKakaoModalOpen, setIsKakaoModalOpen] = useState(false);

  const {
    attribute: emailInputAttribute,
    value: emailInputValue,
    isValidSubmit: isValidSubmitEmail,
  } = useInput({
    initialValue: lastSignInEmail || email || "",
    placeholder: t("이메일을 입력해주세요."),
    submitValidator: validateEmail,
  });

  const {
    attribute: passwordInputAttribute,
    value: passwordInputValue,
    isValidSubmit: isValidSubmitPassword,
  } = useInput({
    initialValue: "",
    placeholder: t("비밀번호를 입력해주세요."),
    submitValidator: (password) => {
      return password.length >= 6;
    },
  });

  // const onKakaoLoginClick = () => {
  //   nativeApi.handleSocialLogin("kakao");
  // };

  const onLoginSubmit = async () => {
    // NOTE: 이메일 검증
    const response = await checkEmail(emailInputValue);
    if (!response.data.duplicated) {
      return toaster.warning(t("존재하지 않는 계정이에요."));
    }
    if (response.data.with === "KAKAO") {
      return setIsKakaoModalOpen(true);
    }
    dispatch(login({ email: emailInputValue, password: passwordInputValue }));
  };

  const [clickCount, setClickCount] = useState(0);

  const handleKakaoLogin = () => {
    // step 1: 아래 코드는 카카오 모듈을 open하고, 유저가 카카오 이메일, 비밀번호를 입력 후 카카오서버는 비컨서버로 아래 redirectUri를 호출합니다.
    window.Kakao.Auth.authorize({
      redirectUri: `${process.env.REACT_APP_API_BASE_URL}/account/auth/kakao/token/b2c`,
    });
    // step 2: 비컨서버는 웹뷰url/auth/kakao/:accessToken 을 FE한테 redirect합니다.
  };

  const testFunction = () => {
    setClickCount((prev) => prev + 1);
    if (clickCount + 1 === 10) {
      handleKakaoLogin();
      setClickCount(0);
    }
  };

  return (
    <section className={styles.loginContainer}>
      {isKakaoModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsKakaoModalOpen(false)}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <p className="whitespace-nowrap text-md tracking-tighter my-text-border-primary-25 font-bold">
              {t("카카오로 가입된 이메일이에요")}
            </p>
            <p className="whitespace-nowrap text-sm  text-base-600 mb-sp-4">{t("카카오로 로그인하시겠어요?")}</p>
            <div className={styles.modalBtns}>
              <BtnV2 onClick={handleKakaoLogin} type="yellow" width="100%" icon={<SvgKakao />}>
                {t("카카오 로그인")}
              </BtnV2>
              <BtnV2 onClick={() => setIsKakaoModalOpen(false)} width="100%">
                {t("닫기")}
              </BtnV2>
            </div>
          </div>
        </div>
      )}
      <div className={styles["box-pageTitle"]} onClick={testFunction}>
        <PageTitle title={isEmailLogin ? t("로그인 또는 회원가입을 해주세요") : t("환영합니다")} />
      </div>
      {!isEmailLogin && (
        <div className={styles["box-loginBtn"]}>
          {i18n.languages[0] === "ko" && (
            <BtnV2 onClick={handleKakaoLogin} type={"yellow"} width={"100%"} icon={<SvgKakao />}>
              {t("카카오로 시작하기")}
            </BtnV2>
          )}
          <BtnV2
            type="purple"
            width={"100%"}
            onClick={() => {
              setIsEmailLogin(true);
            }}
            icon={<SvgMail />}
          >
            {t("이메일로 시작하기")}
          </BtnV2>
        </div>
      )}
      {isEmailLogin && (
        <>
          <div className={styles.inputWrapper}>
            <div className={styles.inputTitle}>{t("이메일")}</div>
            <TextInput attribute={emailInputAttribute} isValidSubmit={isValidSubmitEmail} />
            <ErrorMsgBox visible={!isValidSubmitEmail}>{t("이메일 형식을 확인해주세요.")}</ErrorMsgBox>
          </div>
          <div className={styles.inputWrapper}>
            <div className={styles.inputTitle}>{t("비밀번호")}</div>
            <TextInput attribute={passwordInputAttribute} isValidSubmit={isValidSubmitPassword} isPassword={true} />
            <ErrorMsgBox visible={!isValidSubmitPassword}>{t("비밀번호를 6자리 이상 입력해주세요.")}</ErrorMsgBox>
          </div>
          <div className={styles["box-accountFeature"]}>
            <button
              onClick={() => {
                navigate("/auth/forgot-email");
              }}
              className={styles.forgotPassword}
              type="button"
            >
              {t("이메일 찾기")}
            </button>
            <div className="separator">|</div>
            <button
              onClick={() => {
                navigate("/auth/forgot-password", {
                  state: { email: emailInputValue },
                });
              }}
              className={styles.forgotPassword}
              type="button"
            >
              {t("비밀번호 찾기")}
            </button>
            <div className="separator">|</div>
            <button
              onClick={() => {
                navigate("/auth/signup", {
                  state: { email: emailInputValue },
                });
              }}
              className={styles.forgotPassword}
              type="button"
            >
              {t("회원가입")}
            </button>
          </div>
          <BtnV2
            isDisabled={
              !isValidSubmitEmail ||
              !isValidSubmitPassword ||
              emailInputValue.length === 0 ||
              passwordInputValue.length === 0
            }
            onClick={onLoginSubmit}
            type="purple"
            width="100%"
          >
            {t("로그인")}
          </BtnV2>
        </>
      )}
    </section>
  );
}
